import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  SupervisedUserCircle as RoleIcon,
  AdminPanelSettings as AdminIcon,
  LockReset as ResetPasswordIcon,
  Key as KeyIcon,
  AccessTime as TimeIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
} from '@mui/icons-material';
import authService from '../../services/authService';

// Interface para usuário
interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'viewer';
  status: 'active' | 'inactive';
  avatar?: string;
  lastLogin?: string;
  createdAt: string;
}

const UserDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        setLoading(true);
        // Em uma aplicação real, buscaríamos do backend
        if (!id) return;

        const response = await authService.getUserById(parseFloat(id));
        const user = response;

        // Simulando uma chamada de API com setTimeout
        // Dados de exemplo
        const mockUser: User = {
          id: Number(id),
          name: user.name || '',
          email: user.email || '',
          role: user.role || 'user',
          status: user.status || 'active',
          lastLogin: user.lastLogin,
          createdAt: user.created_at
        };

        setUser(mockUser);
        setLoading(false);

      } catch (error) {
        console.error('Erro ao carregar detalhes do usuário:', error);
        setError('Não foi possível carregar os detalhes do usuário. Tente novamente mais tarde.');
        setLoading(false);
      }
    };

    fetchUserDetails();
  }, [id]);

  const handleEditUser = () => {
    navigate(`/usuarios/editar/${id}`);
  };

  const handleBackToList = () => {
    navigate('/usuarios');
  };

  const handleOpenResetPasswordDialog = () => {
    setResetPasswordDialogOpen(true);
  };

  const handleCloseResetPasswordDialog = () => {
    setResetPasswordDialogOpen(false);
  };

  const handleResetPassword = () => {
    // Em uma aplicação real, aqui faria a chamada para redefinir a senha
    alert(`Enviando e-mail de redefinição de senha para ${user?.email}`);
    setResetPasswordDialogOpen(false);
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteUser = () => {
    // Em uma aplicação real, aqui faria a chamada para excluir o usuário
    alert(`Excluindo usuário ${user?.name}`);
    setDeleteDialogOpen(false);
    // Redireciona de volta para a lista após excluir
    setTimeout(() => navigate('/usuarios'), 500);
  };

  function formatDateBR(dateTimeStr: string): string {
    if (!dateTimeStr) return '';

    // Transformar em formato ISO para o JS
    const date = new Date(dateTimeStr.replace(' ', 'T'));

    // Checa se a data é válida
    if (isNaN(date.getTime())) return '';

    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }


  // Formatar data
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const cleanDateString = dateString.split('T')[0];
      const [year, month, day] = cleanDateString.split('-');
      return `${day}/${month}/${year}`;
    } catch (error) {
      return '-';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} thickness={4} sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
          {error}
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
          Usuário não encontrado.
        </Alert>
        <Button startIcon={<BackIcon />} onClick={handleBackToList}>
          Voltar para lista
        </Button>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={800}>
      <div>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBackToList}
            sx={{ mr: 2 }}
            color="inherit"
          >
            Voltar
          </Button>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
            Detalhes do Usuário
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Redefinir senha">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ResetPasswordIcon />}
                onClick={handleOpenResetPasswordDialog}
                sx={{ borderRadius: '8px' }}
              >
                Redefinir Senha
              </Button>
            </Tooltip>
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEditUser}
              sx={{ borderRadius: '8px' }}
            >
              Editar
            </Button>
          </Box>
        </Box>

        <Paper
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mb: 4,
          }}
        >
          {/* Cabeçalho com informações principais */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 4 }}>
            <Avatar
              sx={{
                bgcolor: user.role === 'admin' ? theme.palette.primary.main : theme.palette.grey[500],
                width: 80,
                height: 80,
                mr: 3,
                fontSize: '2.5rem',
              }}
            >
              {user.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                {user.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                <Chip
                  icon={user.role === 'admin' ? <AdminIcon /> : <PersonIcon />}
                  label={user.role === 'admin' ? 'Administrador' : user.role === 'viewer' ? 'Visualizador' : 'Usuário'}
                  color={user.role === 'admin' ? 'primary' : 'default'}
                  sx={{ fontWeight: 'medium' }}
                />
                <Chip
                  icon={user.status === 'active' ? <ActiveIcon /> : <BlockIcon />}
                  label={user.status === 'active' ? 'Ativo' : 'Inativo'}
                  color={user.status === 'active' ? 'success' : 'error'}
                  sx={{ fontWeight: 'medium' }}
                />
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Informações detalhadas */}
          <Typography variant="h6" component="h3" sx={{ mb: 3 }}>
            Informações do Usuário
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="E-mail"
                        secondary={user.email}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <RoleIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Papel"
                        secondary={user.role === 'admin' ? 'Administrador' : user.role === 'viewer' ? 'Visualizador' : 'Usuário'}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <ActiveIcon
                          color={user.status === 'active' ? 'success' : 'error'}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary="Status"
                        secondary={user.status === 'active' ? 'Ativo' : 'Inativo'}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{
                          variant: 'body1',
                          fontWeight: 'medium',
                          color: user.status === 'active' ? 'success.main' : 'error.main'
                        }}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: '12px', height: '100%' }}>
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Último acesso"
                        secondary={formatDateBR(user.lastLogin ?? '')}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <TimeIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Data de cadastro"
                        secondary={formatDate(user.createdAt)}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                      />
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <KeyIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Senha"
                        secondary="••••••••"
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'body1', fontWeight: 'medium' }}
                      />
                      <Button
                        size="small"
                        startIcon={<ResetPasswordIcon />}
                        onClick={handleOpenResetPasswordDialog}
                      >
                        Redefinir
                      </Button>
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Botões de ação na parte inferior */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={handleBackToList}
              sx={{ borderRadius: '8px' }}
            >
              Voltar
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleOpenDeleteDialog}
                sx={{ borderRadius: '8px' }}
              >
                Excluir Usuário
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleEditUser}
                sx={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.25)',
                }}
              >
                Editar Usuário
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Diálogo de redefinição de senha */}
        <Dialog
          open={resetPasswordDialogOpen}
          onClose={handleCloseResetPasswordDialog}
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle>Redefinir senha</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Deseja enviar um e-mail de redefinição de senha para <strong>{user.email}</strong>? O usuário receberá instruções para criar uma nova senha.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={handleCloseResetPasswordDialog} sx={{ borderRadius: '8px' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleResetPassword}
              variant="contained"
              color="primary"
              sx={{ borderRadius: '8px' }}
            >
              Enviar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Diálogo de exclusão */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle>Excluir usuário</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir o usuário <strong>{user.name}</strong>? Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={handleCloseDeleteDialog} sx={{ borderRadius: '8px' }}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteUser}
              variant="contained"
              color="error"
              sx={{ borderRadius: '8px' }}
            >
              Excluir
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </Fade>
  );
};

export default UserDetails; 